import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, Plus, MessageCircle, User, AlertCircle, Send, Bug, Ban, Trophy, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate } from '../utils/dateUtils';

export const ForumPage = () => {
  const { API, token, user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'General Discussion'
  });
  const [reportData, setReportData] = useState({
    issue_type: 'Bug',
    description: '',
    page_url: ''
  });
  const [reportingPost, setReportingPost] = useState(null);

  const categories = [
    'General Discussion',
    'Treatment Experiences',
    'Symptom Management',
    'Diet & Nutrition',
    'Success Stories',
    'Support & Encouragement'
  ];

  const issueTypes = [
    'Bug',
    'Feature Request',
    'Content Issue',
    'Display Problem',
    'Other'
  ];

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      // Use filtered endpoint to exclude blocked users
      const response = await axios.get(`${API}/forum/posts/filtered${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load forum posts');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async (e, userId, userName) => {
    e.stopPropagation(); // Prevent navigating to post
    if (!window.confirm(`Block ${userName}? You won't see their posts or replies.`)) return;
    
    try {
      await axios.post(`${API}/users/block/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} has been blocked`);
      fetchPosts(); // Refresh to hide blocked user's posts
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleReportPost = async (e, post) => {
    e.stopPropagation(); // Prevent navigating to post
    const reason = window.prompt(`Why are you reporting this post by ${post.user_name}?\n\nPlease describe the issue:`);
    if (!reason) return;
    
    try {
      await axios.post(`${API}/feedback/report`, {
        issue_type: 'Reported Post',
        description: `Reported post: "${post.title}" by ${post.user_name}\n\nReason: ${reason}\n\nPost content: ${post.content?.substring(0, 200)}...`,
        page_url: `/forum/${post.id}`,
        user_email: user?.email,
        user_name: user?.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thank you! Your report has been submitted for review.');
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/feedback/report`, {
        ...reportData,
        user_email: user?.email,
        user_name: user?.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thank you! Your report has been submitted.');
      setReportDialogOpen(false);
      setReportData({ issue_type: 'Bug', description: '', page_url: '' });
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/forum/posts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post created successfully');
      setDialogOpen(false);
      setFormData({ title: '', content: '', category: 'General Discussion' });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
  };

  return (
    <div className="space-y-8" data-testid="forum-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-secondary/20 to-primary/10 rounded-xl p-8 md:p-12">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <Users className="w-12 h-12 text-primary" strokeWidth={1.5} />
              <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Community Forum
              </h1>
            </div>
            <p className="text-lg text-[#57534E]">
              Connect with other parents, share experiences, and find support
            </p>
          </div>
          <div className="flex gap-3">
            {/* Success Stories Link */}
            <Link to="/success-stories">
              <Button
                variant="outline"
                className="rounded-full px-6 py-6 font-medium border-amber-300 text-amber-600 hover:bg-amber-50"
                data-testid="success-stories-button"
              >
                <Trophy className="w-5 h-5 mr-2" />
                Success Stories
              </Button>
            </Link>
            {/* Report Issue Button */}
            <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full px-6 py-6 font-medium border-orange-300 text-orange-600 hover:bg-orange-50"
                  data-testid="report-issue-button"
                >
                  <Bug className="w-5 h-5 mr-2" />
                  Report Issue
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-nunito font-bold flex items-center gap-2">
                    <AlertCircle className="w-6 h-6 text-orange-500" />
                    Report an Issue
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleReportSubmit} className="space-y-5">
                  <p className="text-sm text-[#57534E]">
                    Found a bug or something not working? Let us know and we'll fix it as soon as possible.
                  </p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="issue_type">Issue Type</Label>
                    <select
                      id="issue_type"
                      value={reportData.issue_type}
                      onChange={(e) => setReportData({ ...reportData, issue_type: e.target.value })}
                      className="w-full h-12 px-4 bg-stone-50 border border-stone-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                    >
                      {issueTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="page_url">Where did this happen? (optional)</Label>
                    <Input
                      id="page_url"
                      value={reportData.page_url}
                      onChange={(e) => setReportData({ ...reportData, page_url: e.target.value })}
                      placeholder="e.g., Dashboard, Knowledge Base, etc."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Describe the issue</Label>
                    <Textarea
                      id="description"
                      value={reportData.description}
                      onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                      placeholder="Please describe what happened and what you expected to happen..."
                      rows={5}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full py-6"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit Report
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* New Post Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-6 font-bold shadow-sm transition-transform hover:scale-[1.02]"
                  data-testid="create-post-button"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-nunito font-bold">Create New Post</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="What's your post about?"
                      required
                      data-testid="post-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full h-12 px-4 bg-stone-50 border border-stone-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                      data-testid="post-category-select"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="content">Content</Label>
                    <Textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Share your thoughts, experiences, or questions..."
                      rows={8}
                      required
                      data-testid="post-content-input"
                    />
                  </div>

                  <Button
                    type="submit"
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-bold"
                  data-testid="submit-post-button"
                >
                  Create Post
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
            selectedCategory === 'all'
              ? 'bg-primary text-white shadow-md'
              : 'bg-white text-[#57534E] border border-stone-200 hover:border-primary'
          }`}
          data-testid="category-all"
        >
          All Posts
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              selectedCategory === cat
                ? 'bg-primary text-white shadow-md'
                : 'bg-white text-[#57534E] border border-stone-200 hover:border-primary'
            }`}
            data-testid={`category-${cat.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-[#A8A29E]">Loading posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
          <Users className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <p className="text-[#57534E] text-lg mb-4">No posts yet in this category</p>
          <p className="text-[#A8A29E] mb-6">Be the first to start a discussion!</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3"
          >
            Create First Post
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Card
              key={post.id}
              className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 hover:shadow-[0_8px_24px_rgba(124,154,146,0.15)] hover:border-primary/30 transition-all duration-300 cursor-pointer"
              onClick={() => navigate(`/forum/${post.id}`)}
              data-testid={`forum-post-${post.id}`}
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] leading-tight flex-1">
                    {post.title}
                  </h3>
                  <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold whitespace-nowrap">
                    {post.category}
                  </span>
                </div>

                <p className="text-[#57534E] leading-relaxed line-clamp-2">
                  {post.content}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                  <div className="flex items-center gap-2 text-[#A8A29E]">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">{post.user_name}</span>
                    <span className="text-sm">•</span>
                    <span className="text-sm">{formatLocalDate(post.created_at)}</span>
                    {/* Block User Button - only show for other users */}
                    {post.user_id !== user?.id && (
                      <>
                        <button
                          onClick={(e) => handleReportPost(e, post)}
                          className="ml-2 p-1 text-stone-400 hover:text-amber-500 hover:bg-amber-50 rounded transition-colors"
                          title="Report this post"
                          data-testid={`report-post-${post.id}`}
                        >
                          <Flag className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleBlockUser(e, post.user_id, post.user_name)}
                          className="p-1 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title={`Block ${post.user_name}`}
                          data-testid={`block-user-${post.user_id}`}
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-primary">
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{post.replies_count} replies</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ForumPage;
